
import { createAppAsyncThunk } from '@/store/thunks/createAppAsyncThunk';
import type { FileMetadata } from '@/types/poc/helia';
import { sendFileAnnouncement } from '@/store/thunks/poc/messaging/announceFileReadyThunk';
import { normalizeCid } from '@/services/poc/cidUtils';
import { hexToBytes } from '@/utils/hex.utils';
import { addFileMetadata, setUploadProgress } from '@/store/slices/poc/heliaSlice';

const PROGRESS_ENCRYPTING_START = 0;
const PROGRESS_UPLOADING_START = 30;
const PROGRESS_ANNOUNCING = 80;

/**
 * Async thunk for adding an encrypted file to Helia/IPFS
 * Encrypts the file for recipients and announces it to the network
 */
export const addFileToHelia = createAppAsyncThunk(
  'helia/addFile',
  async(
    { sixId, allParticipantIds, file, path, uploadId: providedUploadId, onProgress }: {
      sixId: string;
      allParticipantIds: string[];
      file: File;
      path: string;
      uploadId?: string;
      onProgress?: (loaded: number, total: number) => void;
    },
    { extra: { pocServices, userService }, dispatch, getState, rejectWithValue }
  ) => {
    const fileName = file.name;
    const uploadId = providedUploadId ?? crypto.randomUUID();

    try {
      // Step 0: Initialize progress
      dispatch(setUploadProgress({
        uploadId,
        fileName,
        path,
        sixId,
        stage: 'encrypting',
        progress: PROGRESS_ENCRYPTING_START
      }));

      // Step 1: Get recipient public keys (ML-KEM encapsulation keys)
      const allParticipantsWithPublicKeys = await Promise.all(
        allParticipantIds.map(async(ownerId) => {
          try {
            const encapsKeyResponse = await userService.getEncapsKey(ownerId);
            const encapsKeyString = encapsKeyResponse.encapsKey;
            const publicKey = new Uint8Array(JSON.parse(encapsKeyString));
            return { id: ownerId, publicKey };
          } catch (apiError) {
            return null;
          }
        })
      )

      // Step 2: Get UKEK and encrypt file for recipients
      const state = getState();
      const ukek = state.auth.ukek;
      const ukekBytes = hexToBytes(ukek);

      const encryptionResult = await pocServices.fileEncryptionService.encryptFile(
        file,
        ukekBytes,
        allParticipantsWithPublicKeys.filter((participant): participant is { id: string; publicKey: Uint8Array } => participant !== null)
      );

      // Update progress: encryption complete, start uploading (animated stage)
      dispatch(setUploadProgress({
        uploadId,
        fileName,
        path,
        sixId,
        stage: 'uploading',
        progress: PROGRESS_UPLOADING_START
      }));

      // Step 3: Add encrypted file and key to IPFS
      const helia = pocServices.p2pStackManagerService.getHeliaNode(sixId);
      if(helia === null) {
        throw new Error('Helia node is not initialized');
      }

      const encryptedFileResult = await pocServices.fileStoreService.addFile(
        helia,
        encryptionResult.encryptedFile,
        onProgress
      );
      const encryptedFileKeyResult = await pocServices.fileStoreService.addFile(
        helia,
        encryptionResult.encryptedFileKey
      );

      // Update progress: IPFS upload complete, announcing to network
      dispatch(setUploadProgress({
        uploadId,
        fileName,
        path,
        sixId,
        stage: 'announcing',
        progress: PROGRESS_ANNOUNCING
      }));

      // Step 4: Create metadata with encryption info
      const cidString = normalizeCid(encryptedFileResult.cid);
      const metadata: FileMetadata = {
        id: cidString, // Use CID as ID for P2P-created files
        cid: cidString,
        name: encryptionResult.originalFileName,
        size: encryptionResult.originalFileSize,
        type: encryptionResult.originalFileType,
        lastModified: Date.now(),
        addedBy: state.user.user?.id ?? 'unknown',
        sixId,
        path,
        encryptedFileKeyCid: normalizeCid(encryptedFileKeyResult.cid),
        recipientEncryption: encryptionResult.recipientResults.map(result => ({
          recipientId: result.recipientId,
          cipherText: Array.from(result.cipherText),
          encryptedFileKey: {
            encryptedData: Array.from(result.encryptedFileKey.encryptedData),
            iv: Array.from(result.encryptedFileKey.iv),
            authTag: Array.from(result.encryptedFileKey.authTag)
          }
        })),
        uploadedByUserId: state.user.user?.id ?? 'unknown',
        uploadedByUserName: state.user.user?.name ?? 'unknown'
      };

      // Step 5: Update state and announce via pubsub
      dispatch(addFileMetadata(metadata));
      void dispatch(sendFileAnnouncement({ sixId, cid: metadata.cid, uploadId, metadata }));

      dispatch(setUploadProgress({
        uploadId,
        fileName,
        path,
        cid: metadata.cid,
        stage: 'announcing',
        progress: PROGRESS_ANNOUNCING,
        sixId,
        metadata
      }));

      return { uploadId, metadata };

    } catch (error) {
      // Update progress: error
      dispatch(setUploadProgress({
        uploadId,
        fileName,
        path,
        sixId,
        stage: 'error',
        progress: 0,
        error: error instanceof Error ? error.message : 'Upload failed'
      }));

      const errorMessage = error instanceof Error ? error.message : 'Unknown error adding encrypted file';
      return rejectWithValue({ message: errorMessage });
    }
  }
);
